import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Wrench, Droplets, RotateCw, Truck, Hammer, Workflow, Cog, Package, HeadphonesIcon, Award, Users, Clock, Shield, ChevronLeft, ChevronRight } from 'lucide-react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';

// Import client logos
import JSWLogo from '../assets/Images/logos/JSW.png';
import MSPLLogo from '../assets/Images/logos/MSPL.png';
import VESCOLogo from '../assets/Images/logos/VESCO.jpg';
import BMMLogo from '../assets/Images/logos/BMM.webp';
import DRNLogo from '../assets/Images/logos/DRN.png';
import HospetSteelsLogo from '../assets/Images/logos/HOSPET_STEELS.jpg';
import KalayniLogo from '../assets/Images/logos/KALAYNI.png';
import KirloskarLogo from '../assets/Images/logos/KIRLOSKAR.png';
import MineraLogo from '../assets/Images/logos/MINERA.webp';
import MukundLogo from '../assets/Images/logos/MUKUND.png';
import SLRLogo from '../assets/Images/logos/SLR.jpg';
import UltratechLogo from '../assets/Images/logos/ULTRATECH.png';
import workshop from '../assets/Images/infra/img1.jpg';
import about1 from '../assets/Images/infra/img6.jpg';
import logo from '../assets/Images/logo.jpg';


import banner2 from '../assets/Images/banner/banner2.webp';
import banner3 from '../assets/Images/banner/banner3.avif';
import banner4 from '../assets/Images/banner/banner4.avif';
import banner5 from '../assets/Images/banner/banner5.jpg';
import banner6 from '../assets/Images/banner/banner6.jpg';


import cylinder from '../assets/Images/services/cylinder.jpg'
import pumps from '../assets/Images/services/pumps.webp'
import motor from '../assets/Images/services/PumpsAndMotors.webp'
import Onsite from '../assets/Images/services/onsite.jpg'
import rocks from '../assets/Images/services/onsite1.webp'
import circuit from '../assets/Images/services/circuitBlocks.webp'
// Custom arrow components
const NextArrow = ({ onClick }) => (
  <div
    className="absolute right-4 top-1/2 -translate-y-1/2 z-10 cursor-pointer bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110 opacity-0 group-hover:opacity-100"
    onClick={onClick}
  >
    <ChevronRight size={24} />
  </div>
);

const PrevArrow = ({ onClick }) => (
  <div
    className="absolute left-4 top-1/2 -translate-y-1/2 z-10 cursor-pointer bg-white/80 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all duration-300 hover:scale-110 opacity-0 group-hover:opacity-100"
    onClick={onClick}
  >
    <ChevronLeft size={24} />
  </div>
);

const Home = () => {
  const navigate = useNavigate();
  const serviceCardsRef = useRef([]);
  const statsRef = useRef([]);
  const whyChooseRef = useRef([]);
  const [counters, setCounters] = useState({});

  // Slider settings
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-up');
            // If it's a stat card, trigger counter
            const statId = entry.target.getAttribute('data-stat-id');
            if (statId) {
              setCounters(prev => ({
                ...prev,
                [statId]: true
              }));
            }
          }
        });
      },
      { threshold: 0.5 } // Trigger when 50% of the element is visible
    );

    [...serviceCardsRef.current, ...statsRef.current, ...whyChooseRef.current].forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const services = [
    {
      icon: <Wrench size={28} />,
      title: "Hydraulic Cylinder Refurbishment",
      description:
        "We specialize in the complete repair, precision rebuilding, and custom manufacturing of single- and double-acting hydraulic cylinders used in heavy-duty industrial machines such as excavators, loaders, and rock breakers.",
      image: cylinder,
      link: '/services/hydraulic-cylinder-refurbishment'
    },
    {
      icon: <Droplets size={28} />,
      title: "Hydraulic Pump Reconditioning",
      description:
        "We provide complete reconditioning, professional repair, precision testing, and reliable supply of all major types of hydraulic pumps, including piston, gear, and vane pumps for diverse heavy industrial applications.",
      image: pumps,
      link: '/services/hydraulic-pump-reconditioning'
    },
    {
      icon: <RotateCw size={28} />,
      title: "Hydraulic Motors Refurbish",
      description:
        "Our hydraulic motor refurbishing service ensures your machinery runs with maximum torque, speed, and reliability. We handle all types of motors—orbital, gear, piston, and vane—for excavators, loaders, crushers, and industrial applications.",
      image: motor,
      link: '/services/hydraulic-motors-refurbish'
    },
    {
      icon: <Truck size={28} />,
      title: "On-Site Hydraulic Services",
      description:
        "We offer on-site hydraulic maintenance and repair services for heavy equipment such as excavators, crushers, loaders, and screening plants. Our skilled technicians are equipped with mobile diagnostic tools and service kits.",
      image: Onsite,
      link: '/services/onsite-hydraulic-services'
    },
    {
      icon: <Hammer size={28} />,
      title: "Rock Breaker Refurbishment",
      description: "Our rock breaker refurbishment service restores impact power, performance, and longevity of all major models. We perform complete overhauls using genuine spares, maximum hitting energy and minimal.",
      image: rocks,
      link: '/services/rock-breaker-refurbishment'
    },
    {
      icon: <Workflow size={28} />,
      title: "Hydraulic Circuit Blocks",
      description:
        "We offer complete refurbishment and testing of hydraulic circuit blocks and manifolds that control the flow of hydraulic fluid in complex systems. Our process ensures correct flow calibration, leak-free operation.",
      image: circuit,
      link: '/services/hydraulic-circuit-blocks'
    },
  ];


  // Clients data
  const clients = [
    {
      id: 1,
      name: 'JSW',
      logo: JSWLogo
    },
    {
      id: 2,
      name: 'MSPL',
      logo: MSPLLogo
    },
    {
      id: 3,
      name: 'VESCO',
      logo: VESCOLogo
    },
    {
      id: 4,
      name: 'BMM',
      logo: BMMLogo
    },
    {
      id: 5,
      name: 'DRN',
      logo: DRNLogo
    },
    {
      id: 6,
      name: 'MINERA',
      logo: MineraLogo
    },
    {
      id: 7,
      name: 'HOSPET STEELS',
      logo: HospetSteelsLogo
    },
    {
      id: 8,
      name: 'KIRLOSKAR',
      logo: KirloskarLogo
    },
    {
      id: 9,
      name: 'SLR',
      logo: SLRLogo

    },
    {
      id: 10,
      name: 'MUKUND',
      logo: MukundLogo
    },
    {
      id: 11,
      name: 'KALAYNI',
      logo: KalayniLogo
    },
    {
      id: 12,
      name: 'ULTRATECH',
      logo: UltratechLogo
    }

  ];

  //   const Counter = ({ target }) => {
  //   const [count, setCount] = React.useState(0);

  //   useEffect(() => {
  //     let start = 0;
  //     const end = parseInt(target);
  //     if (isNaN(end)) return;

  //     const duration = 2000; // 2 seconds
  //     const incrementTime = 20;
  //     const increment = end / (duration / incrementTime);

  //     const counter = setInterval(() => {
  //       start += increment;
  //       if (start >= end) {
  //         clearInterval(counter);
  //         setCount(end);
  //       } else {
  //         setCount(Math.ceil(start));
  //       }
  //     }, incrementTime);

  //     return () => clearInterval(counter);
  //   }, [target]);

  //   return <>{count.toLocaleString()}+</>;
  // };
  const stats = [
    { id: 'clients', icon: <Users size={32} />, number: 500, label: 'Satisfied Clients', animate: true },
    { id: 'experience', icon: <Award size={32} />, number: 20, label: 'Years Experience', animate: true },
    { id: 'equipment', icon: <Cog size={32} />, number: 5000, label: 'Equipment Serviced', animate: true },
    { id: 'projects', icon: <Shield size={32} />, number: 500, label: 'Projects Completed', animate: true },
  ];

  // Counter component
  const Counter = ({ target, id }) => {
    const [count, setCount] = useState(0);
    const duration = 2000; // Animation duration in ms
    const frameDuration = 1000 / 60; // 60fps
    const totalFrames = Math.round(duration / frameDuration);
    const easeOutQuad = t => t * (2 - t);

    useEffect(() => {
      if (counters[id]) {
        let frame = 0;
        const countTo = parseInt(target, 10);
        const counter = setInterval(() => {
          frame++;
          const progress = easeOutQuad(frame / totalFrames);
          const currentCount = Math.round(countTo * progress);

          if (parseInt(count, 10) !== currentCount) {
            setCount(currentCount);
          }

          if (frame === totalFrames) {
            clearInterval(counter);
          }
        }, frameDuration);

        return () => clearInterval(counter);
      }
    }, [counters, id, target]);

    return <span>{count.toLocaleString()}+</span>;
  };

  const aboutImages = [
    about1,
    'https://adamexmaszyny.pl/wp-content/uploads/2019/12/extec-c10_2.png',
    'https://www.rockprocessing.sandvik/contentassets/655693c4c37a4382ac8a2c29f005558c/qa335_sand_001_1600px.jpg?width=1200&height=630&rmode=crop&rsampler=bicubic&compand=true&quality=90&format=webp&v=1695232321&hmac=bfdfa58021d1655ccefdd7d98ded14133cb48fe4e76bbef8ab24fcf4c5e36a76',
    'https://www.powerscreen.com/images/librariesprovider18/product-image-thumbnails/chieftain-600-screen.tmb-1024x.jpg?sfvrsn=b6f6b1d3_11',

  ];

  const whyChooseUs = [
    {
      icon: <Award className="text-[#F5A623]" size={40} />,
      title: '15+ Years Expertise',
      description: 'Our certified technicians bring extensive experience in hydraulic system repairs and maintenance.'
    },
    // {
    //   icon: <Clock className="text-[#F5A623]" size={40} />,
    //   title: '24/7 Emergency Support',
    //   description: 'Round-the-clock service to minimize your downtime and keep operations running smoothly.'
    // },
    // {
    //   icon: <Shield className="text-[#F5A623]" size={40} />,
    //   title: '100% Satisfaction',
    //   description: 'We guarantee top-quality service with a comprehensive 6-month warranty on all repairs.'
    // },
    {
      icon: <Cog className="text-[#F5A623]" size={40} />,
      title: 'Advanced Equipment',
      description: 'State-of-the-art tools and technology for precise diagnostics and repairs.'
    },
    {
      icon: <Truck className="text-[#F5A623]" size={40} />,
      title: 'Mobile Service Units',
      description: 'On-site repairs and maintenance at your location for maximum convenience.'
    },
    {
      icon: <HeadphonesIcon className="text-[#F5A623]" size={40} />,
      title: 'Dedicated Support',
      description: 'Personalized service with a dedicated account manager for all your needs.'
    }
  ];

  // Hero Carousel Component
  const HeroCarousel = () => {
    const slides = [
      {
        id: 1,
        image: logo,
        // "https://images.unsplash.com/photo-1712252278893-c2105d27aed9?q=80&w=1176&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
        alt: "Heavy Equipment 1",
        title: "Hospet Hydraulics Solutions",
        subtitle: "Fluid Expertise, Trusted Solutions",
        description: "Professional maintenance and repair services for all your heavy equipment needs",
        primaryBtn: { text: "Explore Our Services", link: "/services" },
        secondaryBtn: { text: "Contact Us", link: "/contact" }
      },
      {
        id: 2,
        image: banner2,
        // image: "https://cdn.pixabay.com/photo/2018/12/15/14/05/excavator-3876923_1280.jpg",
        alt: "Heavy Equipment 2",
        title: "Advanced Screening Solutions",
        subtitle: "Fluid Expertise, Trusted Solutions",
        description: "Rapid response team available for your urgent repair needs",
        primaryBtn: { text: "Our Services", link: "/services" },
        secondaryBtn: { text: "Get Help Now", link: "/contact" }
      },
      {
        id: 3,
        // image: "https://cdn.pixabay.com/photo/2016/11/29/04/15/digger-1867268_1280.jpg",
        image: banner3,
        alt: "Heavy Equipment 3",
        title: "Genuine Spare Parts",
        subtitle: "Fluid Expertise, Trusted Solutions",
        description: "High-quality OEM and aftermarket parts for all major equipment brands",
        primaryBtn: { text: "View Parts Catalog", link: "/services" },
        secondaryBtn: { text: "Request a Quote", link: "/contact" }
      },
      {
        id: 4,
        // image: "https://cdn.pixabay.com/photo/2017/08/25/21/27/construction-2681433_1280.jpg",
        image: banner4,
        alt: "Heavy Equipment 4",
        title: "Expert Equipment Technicians",
        subtitle: "Fluid Expertise, Trusted Solutions",
        description: "Certified professionals with years of experience in heavy equipment maintenance and repair",
        primaryBtn: { text: "View Our Team", link: "/about" },
        secondaryBtn: { text: "Request Service", link: "/contact" }
      },
      {
        id: 5,
        image: banner5,
        alt: "Heavy Equipment 5",
        title: "Advanced Crushing Solutions",
        subtitle: "Fluid Expertise, Trusted Solutions",
        description: "State-of-the-art multi-stage crushing processes for maximum efficiency and output",
        primaryBtn: { text: "Explore Services", link: "/services" },
        secondaryBtn: { text: "Get a Quote", link: "/contact" }
      },
      {
        id: 6,
        image: banner6,
        alt: "Heavy Equipment 6",
        title: "Advanced Crushing Solutions",
        subtitle: "Fluid Expertise, Trusted Solutions",
        description: "State-of-the-art multi-stage crushing processes for maximum efficiency and output",
        primaryBtn: { text: "Explore Services", link: "/services" },
        secondaryBtn: { text: "Get a Quote", link: "/contact" }
      },
      {
        id: 7,
        image: workshop,
        alt: "Workshop Facility",
        title: "State-of-the-Art Workshop",
        subtitle: "Fluid Expertise, Trusted Solutions",
        description: "Our fully-equipped workshop provides comprehensive repair and maintenance services with precision and care",
        primaryBtn: { text: "View services", link: "/services" },
        secondaryBtn: { text: "Schedule Service", link: "/contact" }
      },

    ];

    const sliderSettings = {
      autoplay: true,
      autoplaySpeed: 3000,
      fade: true,
      arrows: true,
      nextArrow: <NextArrow />,
      prevArrow: <PrevArrow />,
      className: "h-full"
    };

    return (
      <section className="relative h-auto min-h-screen group">
        <Slider {...sliderSettings}>
          {slides.map((slide) => (
            <div key={slide.id} className="relative h-screen w-full">
              <div className="absolute inset-0 bg-black/50 z-10"></div>
              <div className="w-full h-full overflow-hidden">
                <motion.div
                  className="w-full h-full"
                  initial={{ scale: 1 }}
                  animate={{ scale: 1.1 }}
                  transition={{ duration: 30, ease: 'linear' }}
                >
                  <img
                    src={slide.image}
                    alt={slide.alt}
                    className="w-full h-full object-cover object-center"
                  />
                </motion.div>
              </div>
              <div className="absolute inset-0 flex items-center justify-center z-20 text-center px-4 py-16 sm:py-0">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                  <h1 className="text-3xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 slide-in-left">
                    {slide.title}
                  </h1>
                  <h2 className="text-yellow-400 text-xl sm:text-2xl md:text-3xl lg:text-4xl font-medium mb-4 sm:mb-6 relative z-30 opacity-100">
                    {slide.subtitle}
                  </h2>
                  <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-200 mb-6 sm:mb-8 px-2 sm:px-0 slide-in-right delay-200">
                    {slide.description}
                  </p>
                  <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 fade-up delay-400">
                    <button
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-base font-medium rounded-md transition duration-300 w-full sm:w-auto"
                      onClick={() => navigate(slide.primaryBtn.link)}
                    >
                      {slide.primaryBtn.text}
                    </button>
                    <button
                      className="px-4 sm:px-6 py-2 sm:py-3 bg-transparent border-2 border-white text-white hover:bg-white hover:text-gray-900 text-sm sm:text-base font-medium rounded-md transition duration-300 w-full sm:w-auto"
                      onClick={() => navigate(slide.secondaryBtn.link)}
                    >
                      {slide.secondaryBtn.text}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </Slider>
      </section>
    );
  };

  return (
    <div className="App">
      <Header />
      <HeroCarousel />

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((stat, index) => (
              <div
                key={stat.id}
                ref={(el) => (statsRef.current[index] = el)}
                data-stat-id={stat.id}
                className="text-center p-4"
              >
                <div className="stat-icon text-blue-600 text-3xl mb-2">{stat.icon}</div>
                <div className="stat-number text-3xl font-bold text-gray-800">
                  {stat.animate ? <Counter target={stat.number} id={stat.id} /> : stat.number}
                </div>
                <div className="stat-label text-sm text-gray-600 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* About Preview Section */}
      <section className="about-preview-section">
        <div className="about-preview-container">
          <div className="about-preview-content">
            <div className="about-preview-text">
              <motion.h2
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: "easeOut" }}
              >
                About Hospet Hydraulics Solutions
              </motion.h2>
              <motion.p
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
              >
                Hospet Hydraulics is a premier service provider specializing in the maintenance and repair of heavy earth-moving equipment. With a deep understanding of complex machinery, we ensure client equipment operates at peak efficiency.
              </motion.p>
              <motion.p
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
              >
                Our team of highly skilled technicians and engineers are regularly trained on the latest hydraulic and heavy equipment systems, providing you with the expertise needed to keep your operations running smoothly.
              </motion.p>
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
              >
                <button className="btn-primary" onClick={() => navigate('/about')}>
                  Learn More About Us
                </button>
              </motion.div>
            </div>
            <div className="w-full">
              {/* 2x2 Image Grid */}
              <motion.div
                className="grid grid-cols-2 gap-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px 0px -50px 0px" }}
                transition={{ duration: 0.6, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
              >
                {aboutImages.map((src, index) => {
                  // Different animation properties for each image
                  const animations = [
                    { // Top-left image: Fade in from top-left
                      initial: { opacity: 0, x: -30, y: -30, rotate: -5 },
                      whileInView: { opacity: 1, x: 0, y: 0, rotate: 0 },
                      transition: {
                        duration: 0.8,
                        delay: 0.1 * index,
                        ease: [0.2, 0.8, 0.2, 1],
                        rotate: { type: 'spring', stiffness: 100, damping: 10 }
                      }
                    },
                    { // Top-right image: Fade in with scale
                      initial: { opacity: 0, scale: 0.8, rotate: 5 },
                      whileInView: { opacity: 1, scale: 1, rotate: 0 },
                      transition: {
                        duration: 0.7,
                        delay: 0.15 + (0.1 * index),
                        ease: [0.2, 0.8, 0.2, 1]
                      }
                    },
                    { // Bottom-left image: Fade in from bottom
                      initial: { opacity: 0, y: 50, scale: 0.9 },
                      whileInView: { opacity: 1, y: 0, scale: 1 },
                      transition: {
                        duration: 0.8,
                        delay: 0.2 + (0.1 * index),
                        ease: [0.2, 0.8, 0.2, 1],
                        y: { type: 'spring', stiffness: 100, damping: 10 }
                      }
                    },
                    { // Bottom-right image: Fade in with blur
                      initial: { opacity: 0, filter: 'blur(4px)' },
                      whileInView: { opacity: 1, filter: 'blur(0px)' },
                      transition: {
                        duration: 1,
                        delay: 0.25 + (0.1 * index),
                        ease: [0.2, 0.8, 0.2, 1],
                        filter: { duration: 0.8 }
                      }
                    }
                  ][index];

                  return (
                    <motion.div
                      key={index}
                      className="relative overflow-hidden rounded-lg aspect-square group"
                      initial={animations.initial}
                      whileInView={animations.whileInView}
                      viewport={{ once: true, margin: "-50px 0px -50px 0px" }}
                      whileHover={{
                        scale: 1.03,
                        transition: {
                          duration: 0.4,
                          ease: [0.4, 0, 0.2, 1]
                        }
                      }}
                      transition={animations.transition}
                    >
                      <motion.img
                        src={src}
                        alt={`About us ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        initial={{ scale: 1 }}
                        whileHover={{
                          scale: 1.1,
                          transition: {
                            duration: 0.6,
                            ease: [0.4, 0, 0.2, 1]
                          }
                        }}
                      />
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview Section */}
      <section className="px-2 sm:px-4 md:px-6 lg:px-8 py-16">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px 0px -50px 0px" }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-center text-[#00325b] mb-4">Our Services</h2>
            <p className="text-gray-600 text-center max-w-3xl mx-auto mb-12">
              Comprehensive solutions for all your heavy equipment needs with expert technicians and quality parts
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 px-2 sm:px-0">
            {services.map((service, index) => (
              <motion.div
                key={index}
                ref={(el) => (serviceCardsRef.current[index] = el)}
                className="bg-white rounded-lg shadow-md overflow-hidden transition-all duration-300 group"
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px 0px -50px 0px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                onClick={() => navigate(service.link)}
                whileHover={{ y: -5, boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)' }}
              >
                <motion.div
                  className="service-image overflow-hidden"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.3 }}
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    loading="lazy"
                    className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                </motion.div>
                <motion.div
                  className="service-content p-6"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 + (index * 0.05) }}
                >
                  <motion.div
                    className="service-icon w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-blue-200 transition-colors duration-300"
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    {service.icon}
                  </motion.div>
                  <h3 className="service-title text-xl font-semibold mb-2 text-gray-900 group-hover:text-[#F5A623] transition-colors">
                    {service.title}
                  </h3>
                  <p className="service-description text-gray-600 mb-4">
                    <span className="hidden sm:inline">{service.description}</span>
                    <span className="sm:hidden">
                      {service.description.split('.').slice(0, 2).join('.')}.
                    </span>
                  </p>
                  <div className="text-blue-600 group-hover:text-[#F5A623] font-medium flex items-center group-hover:translate-x-1 transition-transform duration-300">
                    Learn more
                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </motion.div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Section */}
      <section className="py-16 bg-white overflow-hidden">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-[#00325b]">Our Valued Clients</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">Trusted by leading companies in the industry</p>
          </motion.div>

          <div className="bg-white">

            <motion.div
              className="flex items-center space-x-16 py-8"
              animate={{
                x: ['0%', '-100%'],
              }}
              transition={{
                x: {
                  repeat: Infinity,
                  repeatType: 'loop',
                  duration: window.innerWidth < 768 ? 10 : 15, // Faster on mobile
                  ease: 'linear',
                },
              }}
            >

              {[...Array(2)].map((_, loopIndex) => (
                <React.Fragment key={`loop-${loopIndex}`}>
                  {clients.map((client, index) => (
                    <motion.div
                      key={`${loopIndex}-${index}`}
                      className="flex-shrink-0 p-2 rounded bg-white"
                      whileHover={{ scale: 1.1, y: -5 }}
                      transition={{ type: 'spring', stiffness: 300 }}
                    >
                      <img
                        src={client.logo}
                        alt={client.name}
                        title={client.name}
                        className="h-16 w-auto object-contain"
                        loading="lazy"
                      />
                    </motion.div>
                  ))}
                </React.Fragment>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block px-4 py-1 mb-4 text-sm font-semibold text-blue-600 bg-blue-100 rounded-full">Why Choose Us</span>
            <h2 className="text-4xl md:text-5xl font-bold text-[#00325b] mb-4">Excellence in Every Service</h2>
            <div className="w-24 h-1.5 bg-blue-600 mx-auto mb-6 rounded-full"></div>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We combine cutting-edge technology with decades of expertise to deliver unmatched hydraulic solutions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-4 sm:gap-6 md:gap-8 w-full max-w-6xl mx-auto">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group bg-white rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 h-full flex flex-col"
              >
                <div className="w-16 h-16 mb-6 rounded-2xl bg-opacity-10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600 leading-relaxed">{item.description}</p>
                <div className="mt-6 pt-4 border-t border-gray-100 group-hover:border-blue-100 transition-colors">
                  <div className="flex items-center text-blue-600 font-medium group-hover:text-blue-700 transition-colors">
                    Learn more
                    <svg className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>


        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-container">
          <h2>Ready to Get Started?</h2>
          <p>Contact us today to discuss your equipment needs and discover how we can help</p>
          <button className="btn-primary" onClick={() => navigate('/contact')}>
            Schedule a Service
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;